import { Bird, ChevronRight, Mail, FileText, MessageSquare, Shield } from "lucide-react";

export function LegalPage({ title, onBack, content }) {
  return (
    <div className="min-h-[100dvh] bg-white" data-testid="legal-page">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center"><Bird className="w-4 h-4 text-slate-950" /></div>
            <span className="text-sm font-black uppercase tracking-tight">Pigeon Supply</span>
          </div>
          <button onClick={onBack} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors flex items-center gap-1">
            <ChevronRight className="w-3 h-3 rotate-180" /> Back
          </button>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{title}</h1>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-10">Last updated: January 2026</p>
        <div className="prose prose-sm max-w-none text-slate-600 leading-relaxed space-y-6">
          {content}
        </div>
      </main>
    </div>
  );
}

export function TermsContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">1. Agreement to Terms</h2>
      <p>By accessing or using Pigeon Supply ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the Service.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">2. Description of Service</h2>
      <p>Pigeon Supply is a logistics management platform designed for construction and fleet operations. The Service provides real-time fleet tracking, driver management, fuel logistics coordination, multi-language support, and communication tools for Planners, Drivers, and Machinists.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">3. Subscriptions and Billing</h2>
      <p>The Service is offered on a subscription basis. Prices are listed in EUR and billed monthly. All payments are processed securely through Stripe. Your subscription will automatically renew unless cancelled before the renewal date. You may cancel your subscription at any time through your account settings.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">4. Free Trial</h2>
      <p>New subscribers may be eligible for a 14-day free trial. No credit card is required for the trial period. At the end of the trial, you must subscribe to continue using the Service.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">5. Acceptable Use</h2>
      <p>You agree not to use the Service for any unlawful purpose or in violation of these Terms. You are responsible for all activity that occurs under your account. You must not share your login credentials with unauthorized parties.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">6. Intellectual Property</h2>
      <p>The Service and its original content, features, and functionality are owned by Pigeon Supply and are protected by international copyright, trademark, and other intellectual property laws.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">7. Data Ownership</h2>
      <p>You retain ownership of all data you input into the Service. We do not claim ownership of your logistics data, site information, or operational records.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">8. Limitation of Liability</h2>
      <p>In no event shall Pigeon Supply be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, resulting from your use of the Service.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">9. Termination</h2>
      <p>We may terminate or suspend your account immediately, without prior notice, if you breach these Terms. Upon termination, your right to use the Service will cease immediately.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">10. Governing Law</h2>
      <p>These Terms shall be governed by the laws of Belgium, without regard to its conflict of law provisions. Any disputes will be resolved in the courts of Belgium.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">11. Contact</h2>
      <p>For questions about these Terms, contact us at: <a href="mailto:legal@onepigeon.app" className="text-red-600">legal@onepigeon.app</a></p>
    </>
  );
}

export function PrivacyContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">1. Information We Collect</h2>
      <p>We collect only the minimum data necessary to provide the Service, in compliance with GDPR and Belgian privacy regulations:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account information: name, email address, company name</li>
        <li>Operational data: site names, machine identifiers, delivery records, driver names</li>
        <li>Usage data: login timestamps, feature usage (anonymized)</li>
        <li>Payment data: processed and stored exclusively by Stripe (we never store card details)</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">2. How We Use Your Data</h2>
      <p>Your data is used exclusively to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Provide and maintain the Service</li>
        <li>Process transactions and send billing notifications</li>
        <li>Improve the Service through anonymized usage analytics</li>
        <li>Communicate important updates about the Service</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">3. Data Storage and Security</h2>
      <p>All data is stored on secure servers within the European Union. We use industry-standard encryption (TLS 1.3) for data in transit and AES-256 encryption for data at rest. Access to production data is restricted to authorized personnel only.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">4. Data Retention</h2>
      <p>We retain your data for the duration of your subscription plus 30 days. After account deletion, all personal data is permanently removed within 30 days. Anonymized operational data may be retained for analytics purposes.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">5. Your Rights (GDPR)</h2>
      <p>Under GDPR, you have the right to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Access your personal data</li>
        <li>Rectify inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Object to processing of your data</li>
        <li>Withdraw consent at any time</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">6. Third-Party Services</h2>
      <p>We use the following third-party services:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe</strong>: Payment processing (PCI DSS Level 1 compliant)</li>
        <li><strong>AI Translation</strong>: Text is processed for translation purposes only and not stored by the AI provider</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">7. Cookies</h2>
      <p>We use only essential cookies required for the Service to function. We do not use tracking cookies or third-party advertising cookies.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">8. Data Protection Officer</h2>
      <p>For privacy inquiries, contact our DPO at: <a href="mailto:privacy@onepigeon.app" className="text-red-600">privacy@onepigeon.app</a></p>
    </>
  );
}

export function RefundContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Refund Policy</h2>
      <p>We want you to be completely satisfied with Pigeon Supply. Here is our refund policy:</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">14-Day Free Trial</h2>
      <p>All new subscriptions include a 14-day free trial. No payment is required during the trial period. If you cancel before the trial ends, you will not be charged.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Monthly Subscriptions</h2>
      <p>For monthly subscriptions, you may request a full refund within 7 days of your payment date if you are not satisfied with the Service. After 7 days, refunds are issued on a prorated basis for the remaining days in the billing period.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">How to Request a Refund</h2>
      <p>To request a refund, contact our support team at <a href="mailto:billing@onepigeon.app" className="text-red-600">billing@onepigeon.app</a> with your account email and reason for the refund. Refunds are processed within 5-10 business days back to the original payment method.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Cancellation</h2>
      <p>You may cancel your subscription at any time. After cancellation, you retain access to the Service until the end of your current billing period. No further charges will be made after cancellation.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Exceptions</h2>
      <p>Refunds may not be available in cases of:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Violation of our Terms of Service</li>
        <li>Fraudulent activity</li>
        <li>Subscriptions active for more than 30 days without prior refund request</li>
      </ul>
    </>
  );
}

export function ContactContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Get in Touch</h2>
      <p>We'd love to hear from you. Whether you have a question about features, pricing, or anything else, our team is ready to answer.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <Mail className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">General Inquiries</h3>
          <a href="mailto:info@onepigeon.app" className="text-sm text-red-600 font-medium">info@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <FileText className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Billing & Payments</h3>
          <a href="mailto:billing@onepigeon.app" className="text-sm text-red-600 font-medium">billing@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <MessageSquare className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Technical Support</h3>
          <a href="mailto:support@onepigeon.app" className="text-sm text-red-600 font-medium">support@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <Shield className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Privacy & GDPR</h3>
          <a href="mailto:privacy@onepigeon.app" className="text-sm text-red-600 font-medium">privacy@onepigeon.app</a>
        </div>
      </div>
      <h2 className="text-lg font-bold text-slate-900 mt-10">Office Address</h2>
      <p className="text-slate-400">Pigeon Supply<br />Supply Management Platform<br />Belgium, EU</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Business Hours</h2>
      <p className="text-slate-400">Monday - Friday: 09:00 - 18:00 CET<br />Weekend: Emergency support only</p>
    </>
  );
}

export function GDPRContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">GDPR Compliance</h2>
      <p>Pigeon Supply is fully compliant with the General Data Protection Regulation (GDPR) of the European Union. We are committed to protecting the privacy and personal data of all users.</p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Data Controller</h2>
      <p>Pigeon Supply, registered in Belgium, EU, acts as the Data Controller for all personal data collected through the Service. Contact: <a href="mailto:privacy@onepigeon.app" className="text-red-600">privacy@onepigeon.app</a></p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Legal Basis for Processing</h2>
      <p>We process personal data based on the following legal grounds:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Contract performance</strong>: Processing necessary to provide the Service you subscribed to</li>
        <li><strong>Legitimate interest</strong>: Service improvement through anonymized analytics</li>
        <li><strong>Consent</strong>: Marketing communications (opt-in only)</li>
        <li><strong>Legal obligation</strong>: Financial record-keeping and tax compliance</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Cookie Policy</h2>
      <p>Pigeon Supply uses only <strong>strictly necessary cookies</strong> required for the platform to function:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Session cookie</strong>: Maintains your authentication state during your visit</li>
        <li><strong>Preference cookie</strong>: Stores your language selection and cookie consent</li>
      </ul>
      <p className="mt-2">We do <strong>not</strong> use:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Third-party tracking cookies</li>
        <li>Advertising cookies</li>
        <li>Analytics cookies (all analytics are server-side and anonymized)</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Your Rights Under GDPR</h2>
      <p>As a data subject in the EU, you have the following rights:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Right of Access</strong> (Art. 15): Request a copy of your personal data</li>
        <li><strong>Right to Rectification</strong> (Art. 16): Correct inaccurate personal data</li>
        <li><strong>Right to Erasure</strong> (Art. 17): Request deletion of your personal data ("right to be forgotten")</li>
        <li><strong>Right to Data Portability</strong> (Art. 20): Receive your data in a machine-readable format</li>
        <li><strong>Right to Restrict Processing</strong> (Art. 18): Limit how we use your data</li>
        <li><strong>Right to Object</strong> (Art. 21): Object to processing based on legitimate interest</li>
        <li><strong>Right to Withdraw Consent</strong> (Art. 7): Withdraw consent at any time</li>
      </ul>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Data Protection Officer</h2>
      <p>Our Data Protection Officer can be reached at: <a href="mailto:dpo@onepigeon.app" className="text-red-600">dpo@onepigeon.app</a></p>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Supervisory Authority</h2>
      <p>If you believe your data protection rights have been violated, you have the right to lodge a complaint with the Belgian Data Protection Authority (Gegevensbeschermingsautoriteit/Autorite de protection des donnees).</p>
    </>
  );
}
